import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MetricSharedLinkWrapper, SharedLinkPeriods } from '../../../dto/MetricSharedLinkWrapper';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { cloneDeep } from 'lodash';
import { UserInfo } from '../../../../auth/service/auth.service';
import { LocalMonthDate } from '../../../../../core/pojo/date-variable';
import { DateService } from "../../../../../util/common/date-service/date.service";
import { UUID } from "../../../../../core/pojo/id-variable";
import { KPI_URLS } from "../../../module/kpi-module-routing.module";
import { ToolTipSettings } from "../../../../shared/component/tooltip/tooltip.util";

export class ModalMetricPublicSharedLinkFormComponent {
  private _destroyStream: Subject<void> = new Subject<void>();

  form: MslFormGroup;

  selectedCompanyUUID: UUID;
  budgetPeriods = new SharedLinkPeriods();
  actualPeriods = new SharedLinkPeriods();
  sharedLinkWrapper: MetricSharedLinkWrapper;
  user: UserInfo;

  tooltipSettings: ToolTipSettings = {
    show: false,
    title: '',
  };

  constructor() {
    this.initForm();
  }

  onDestroy(): void {
    this._destroyStream.next();
  }

  initializeValues(
    selectedCompanyUUID: UUID,
    periods: {
      actual: SharedLinkPeriods;
      budget: SharedLinkPeriods;
    },
    sharedLinkWrapper: MetricSharedLinkWrapper,
    user: UserInfo): void {
    this.selectedCompanyUUID = selectedCompanyUUID;
    this.actualPeriods = cloneDeep(periods.actual);
    this.budgetPeriods = cloneDeep(periods.budget);
    this.sharedLinkWrapper = sharedLinkWrapper;
    this.user = cloneDeep(user);
    this.initForm();
  }

  initForm(): void {
    this.form = new FormGroup({
      uuid: new FormControl(this.sharedLinkWrapper?.uuid),
      companyUUID: new FormControl(this.selectedCompanyUUID),
      actual: new FormGroup({
        isActivated: new FormControl({
          value: this.sharedLinkWrapper?.actual.isActivated ?? !this.isActualIsActivatedDisabled(),
          disabled: this.isActualIsActivatedDisabled(),
        }),
        fromPeriod: new FormControl(
          DateService.toLocalMonthDate(this.sharedLinkWrapper?.actual.fromPeriod), {
            nonNullable: true,
            validators: [Validators.required],
          }),
        tillPeriod: new FormControl(
          DateService.toLocalMonthDate(this.sharedLinkWrapper?.actual.tillPeriod ?? this.actualPeriods.all?.at(-1)), {
            nonNullable: true, validators: [Validators.required],
          }),
      }),
      budget: new FormGroup({
        isActivated: new FormControl({
          value: this.sharedLinkWrapper?.budget.isActivated ?? !this.isBudgetIsActivatedDisabled(),
          disabled: this.isBudgetIsActivatedDisabled(),
        }),
        fromPeriod: new FormControl(
          DateService.toLocalMonthDate(this.sharedLinkWrapper?.budget.fromPeriod), {
            nonNullable: true,
            validators: [Validators.required],
          }),
        tillPeriod: new FormControl(
          DateService.toLocalMonthDate(this.sharedLinkWrapper?.budget.tillPeriod ?? this.budgetPeriods.all?.at(-1)), {
            nonNullable: true,
            validators: [Validators.required],
          }),
      }),
      contact: new FormGroup({
        isActivated: new FormControl(this.sharedLinkWrapper?.contact.isActivated ?? true),
        name: new FormControl(this.sharedLinkWrapper?.contact.name ?? this.user?.name),
        email: new FormControl(this.sharedLinkWrapper?.contact.email ?? this.user?.mail, { validators: [Validators.email] }),
      }),
      password: new FormControl({ value: this.sharedLinkWrapper?.password, disabled: true }),
      token: new FormControl({
        value: this.sharedLinkWrapper ?
          `${document.location.origin}/${KPI_URLS.base}/${KPI_URLS._external}?token=${this.sharedLinkWrapper.token}` : undefined,
        disabled: true,
      }),
      lastModifiedDate: new FormControl(this.sharedLinkWrapper?.lastModifiedDate),
      creationDate: new FormControl(this.sharedLinkWrapper?.creationDate),
    });
    this.setFormTriggers();
    this.updatePeriodSelector('budget', this.sharedLinkWrapper?.budget.fromPeriod, this.sharedLinkWrapper?.budget.tillPeriod);
    this.updatePeriodSelector('actual', this.sharedLinkWrapper?.actual.fromPeriod, this.sharedLinkWrapper?.actual.tillPeriod);
    this.checkPlanningControlsDisability();
    this.checkContactControlsDisability();
  }

  /**
   * Restricts the periods in the app-selector for consistent state.
   * After period select we want to be sure that user cannot choose the wrong period.
   * E.x.
   * after choosing "till period" 2023-01, user should have no options for "from period" after 2023-01.
   *
   * @param planning determines, which selector group has asked for the map.
   * @param fromPeriod is nullable and can be the same as tillPeriod
   * @param tillPeriod is nullable and can be the same as fromPeriod
   */
  updatePeriodSelector(planning: PublicSharedLinkPlanning, fromPeriod?: LocalMonthDate, tillPeriod?: LocalMonthDate): void {
    if (!this.actualPeriods.all) {
      return;
    }
    fromPeriod = fromPeriod ?? (planning === 'actual' ? this.actualPeriods.all.at(0) : this.budgetPeriods.all.at(0));
    tillPeriod = tillPeriod ?? (planning === 'actual' ? this.actualPeriods.all.at(-1) : this.budgetPeriods.all.at(-1));
    if (planning === 'actual') {
      this.actualPeriods.from = this.actualPeriods.all.filter((period) => period <= tillPeriod);
      this.actualPeriods.till = this.actualPeriods.all.filter((period) => period >= fromPeriod);
    } else {
      this.budgetPeriods.from = this.budgetPeriods.all.filter((period) => period <= tillPeriod);
      this.budgetPeriods.till = this.budgetPeriods.all.filter((period) => period >= fromPeriod);
    }
  }

  canToggle(): boolean {
    if (this.doesLinkExists()) {
      return true;
    }

    return this.isAtLeastOnePlanningSelected();
  }

  /**
   * Preselects the "from period" when:
   * 1) planning is activated.
   * 2) no "from period" is selected.
   * 3) at least one period to choose from exists.
   */
  preSelectFromPeriod(): void {
    const budget = this.form.getRawValue().budget;
    if (budget.isActivated && !budget.fromPeriod && this.budgetPeriods.from.length) {
      this.form.controls.budget.controls.fromPeriod.patchValue(this.budgetPeriods.from[0]);
    }
    const actual = this.form.getRawValue().actual;
    if (actual.isActivated && !actual.fromPeriod && this.actualPeriods.from.length) {
      this.form.controls.actual.controls.fromPeriod.patchValue(this.actualPeriods.from[0]);
    }
  }

  /**
   * Provides the current (disabled or not) state of the form controls.
   *
   * @param type is the name of the form control group.
   * @apiNote contactEmail was decided to change to false, but might be changed later due to no email validation.
   * With a better UI/UX this will be changed later.
   */
  isDisabled(type: 'actual' | 'budget' | 'contactEmail'): boolean {
    const formControls = this.form.controls;
    const formValue = this.form.getRawValue();
    switch (type) {
      case 'actual':
        return formValue.actual.isActivated && !formControls.actual.valid;
      case 'budget':
        return formValue.budget.isActivated && !formControls.budget.valid;
      case 'contactEmail':
        return false; // is a matter of discussion for future. Email is not validated for now.
    }
  }

  getFormRawValue(): MetricSharedLinkWrapper {
    const form = this.form.getRawValue();
    this.updatePeriodsToBeLocalDate(form);
    this.updateIsActivated(form);
    return form;
  }

  activateTooltip(id: string): void {
    if (!this.form.valid) {
      this.tooltipSettings.show = true;
      this.tooltipSettings.coordinates = document.getElementById(id).getBoundingClientRect();
    }
  }

  private updateIsActivated(form: MetricSharedLinkWrapper): void {
    if (!form.actual.tillPeriod || !form.actual.fromPeriod) {
      form.actual.isActivated = false;
    }
    if (!form.budget.tillPeriod || !form.budget.fromPeriod) {
      form.budget.isActivated = false;
    }
  }

  /**
   * Updates form' selected periods to be of type YYYY-MM-01.
   *
   * For simplicity, we operate with LocalMonthDate, since the day is constant: '-01'.
   * To create/update the periods, we need to make sure that our dates are still in LocalDate format.
   * Therefore, use this function when you need to update the form periods to be LocalDate format.
   * @param form with the periods. Periods can be null.
   */
  private updatePeriodsToBeLocalDate(form: MetricSharedLinkWrapper): void {
    form.actual.fromPeriod = DateService.toLocalDate(form.actual.fromPeriod);
    form.budget.fromPeriod = DateService.toLocalDate(form.budget.fromPeriod);
    form.actual.tillPeriod = DateService.toLocalDate(form.actual.tillPeriod);
    form.budget.tillPeriod = DateService.toLocalDate(form.budget.tillPeriod);
  }

  private isAtLeastOnePlanningSelected(): boolean {
    const form = this.form.getRawValue();
    return form.actual.isActivated || form.budget.isActivated;
  }

  private doesLinkExists(): boolean {
    return Boolean(this.sharedLinkWrapper);
  }

  private setFormTriggers(): void {
    // resets previous subscriptions.
    this.onDestroy();
    this.form.valueChanges
      .pipe(takeUntil(this._destroyStream))
      .subscribe(() => {
        this.tooltipSettings.title = this.getTooltipText();
      });
    this.form.controls.actual.controls.isActivated.valueChanges
      .pipe(takeUntil(this._destroyStream))
      .subscribe({
        next: () => {
          this.checkPlanningControlsDisability();
        },
      });
    this.form.controls.budget.controls.isActivated.valueChanges
      .pipe(takeUntil(this._destroyStream))
      .subscribe({
        next: () => {
          this.checkPlanningControlsDisability();
        },
      });
    this.form.controls.contact.controls.isActivated.valueChanges
      .pipe(takeUntil(this._destroyStream))
      .subscribe({
        next: () => {
          this.checkContactControlsDisability();
        },
      });
  }

  /**
   * After the 'Actual' or 'Budget' buttons are clicked,
   * the "from-"/"till periods" controls should be blocked/unlocked.
   *
   * controlName is strictly typed, so the typescript knows the correct control.
   * Therefore, the namings must be same as in the form group control.
   *
   * isEnabled if true -> unlocks the controls. Else -> blocks.
   */
  private checkPlanningControlsDisability(): void {
    const plannings: PublicSharedLinkPlanning[] = ['budget', 'actual'];
    plannings.forEach((planning) => {
      const control = this.form.controls[planning].controls;
      if (control.isActivated.value) {
        control.tillPeriod.enable();
        control.fromPeriod.enable();
      } else {
        control.tillPeriod.disable();
        control.fromPeriod.disable();
      }
    });
  }

  private checkContactControlsDisability(): void {
    if (this.form.getRawValue().contact.isActivated) {
      this.form.controls.contact.controls.email.enable();
      this.form.controls.contact.controls.name.enable();
    } else {
      this.form.controls.contact.controls.email.disable();
      this.form.controls.contact.controls.name.disable();
    }
  }

  private isActualIsActivatedDisabled(): boolean {
    const arePeriodsGiven = Boolean(this.actualPeriods.all && this.actualPeriods.all.length);
    return !arePeriodsGiven;
  }

  private isBudgetIsActivatedDisabled(): boolean {
    const arePeriodsGiven = Boolean(this.budgetPeriods.all && this.budgetPeriods.all.length);
    return !arePeriodsGiven;
  }

  private getTooltipText(): string {
    const isActual = this.isDisabled('actual') ? 'Actual period' : '';
    const isBudget = this.isDisabled('budget') ? 'Budget period' : '';
    const isContactEmail = this.isDisabled('contactEmail') ? 'Contact person email' : '';
    const errors = [isActual, isBudget, isContactEmail].filter((error) => error).join(', ');
    return `Please check: ${errors}`;
  }
}

export type PublicSharedLinkPlanning = 'actual' | 'budget';

export type MslPeriodsFormGroup = FormGroup<{
  isActivated: FormControl<boolean>;
  fromPeriod: FormControl<LocalMonthDate>;
  tillPeriod: FormControl<LocalMonthDate>;
}>;

export type MslContactFormGroup = FormGroup<{
  isActivated: FormControl<boolean>;
  name: FormControl<string>;
  email: FormControl<string>;
}>;

export type MslFormGroup = FormGroup<{
  uuid: FormControl<string>;
  companyUUID: FormControl<string>;
  actual: MslPeriodsFormGroup
  budget: MslPeriodsFormGroup;
  contact: MslContactFormGroup
  password: FormControl<string>;
  token: FormControl<string>;
  lastModifiedDate: FormControl<Date>;
  creationDate: FormControl<Date>;
}>;
